// src/context/AuthContext.js
import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import api, { register401Handler } from '../utils/api';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
    // Initialize user state from localStorage
    const [user, setUser] = useState(() => {
        try {
            const storedUser = localStorage.getItem('user');
            return storedUser ? JSON.parse(storedUser) : null;
        } catch (e) {
            console.error("Failed to parse user from localStorage", e);
            localStorage.removeItem('user');
            return null;
        }
    });

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [enrolledCourses, setEnrolledCourses] = useState([]);

    const hasCheckedAuth = React.useRef(false);

    const checkAuth = useCallback(async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                setUser(null);
                setLoading(false);
                return false;
            }
            
            const response = await api.get('/auth/check');

            if (response.data.success) {
                // The user from the backend is the source of truth.
                const backendUser = response.data.user;
                setUser(backendUser);
                localStorage.setItem('user', JSON.stringify(backendUser));
            } else {
                // Clear everything if the token is invalid
                localStorage.removeItem('token');
                localStorage.removeItem('user');
                localStorage.removeItem('role');
                setUser(null);
            }
        } catch (error) {
            console.error('Auth check error details:', error.response || error.message);
            
            // On auth failure, clear storage and user state
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            localStorage.removeItem('role');
            setUser(null);
        } finally {
            setLoading(false);
        }
    }, []);
    
    const login = async (emailOrToken, password) => {
        setLoading(true);
        try {
            setError(null);
    
            if (emailOrToken && !password) {
                localStorage.setItem('token', emailOrToken);
                await checkAuth(); // checkAuth will now set user and localStorage
                toast.success('Login successful!');
                return true;
            }
    
            const response = await api.post('/auth/login', { email: emailOrToken, password });
            if (response.data.success) {
                const loggedInUser = response.data.user;
                setUser(loggedInUser);
                localStorage.setItem('token', response.data.token);
                localStorage.setItem('role', loggedInUser?.role);
                localStorage.setItem('user', JSON.stringify(loggedInUser)); // Store user object
                
                toast.success('Login successful!');
                await fetchUserCourses(loggedInUser.id);
                return true;
            }
            return false;
        } catch (error) {
            console.error('Login error:', error);
            const message = error.response?.data?.message || 'Login failed. Please try again.';
            setError(message);
            toast.error(message);
            return false;
        } finally {
            setLoading(false);
        }
    };
    
    const register = async (userData) => {
        setLoading(true);
        try {
            setError(null);
            const response = await api.post('/auth/register', userData);
            if (response.data.success) {
                const registeredUser = response.data.user;
                setUser(registeredUser);
                // The backend doesn't automatically log in, so we just show success.
                // No token or user is stored here. They must log in after registering.
                toast.success('Registration successful! Please log in.');
                return true;
            }
            return false;
        } catch (error) {
            console.error('Registration error:', error);
            const message = error.response?.data?.message || 'Registration failed. Please try again.';
            setError(message);
            toast.error(message);
            return false;
        } finally {
            setLoading(false);
        }
    };
    
    const logout = async () => {
        try {
            // Optional: notify backend of logout
            // await api.get('/auth/logout'); 
        } catch (error) {
            console.error('Server logout failed, proceeding with client-side cleanup.', error);
        } finally {
            // Clear client-side state and storage regardless
            setUser(null);
            localStorage.removeItem('user');
            localStorage.removeItem('token');
            localStorage.removeItem('role');
            setEnrolledCourses([]);
            toast.success('Logged out successfully');
            // Redirect to login page to ensure clean state
            if (window.location.pathname !== '/auth') {
                window.location.href = '/auth';
            }
        }
    };
    
    const updateProfile = async (userData) => {
        setLoading(true);
        try {
            setError(null);
            const response = await api.put('/users/profile', userData);
            if (response.data.success) {
                const updatedUser = response.data.user;
                setUser(updatedUser);
                localStorage.setItem('user', JSON.stringify(updatedUser)); // Update localStorage
                toast.success('Profile updated successfully!');
                return true;
            }
            return false;
        } catch (error) {
            console.error('Profile update error:', error);
            const message = error.response?.data?.message || 'Failed to update profile. Please try again.';
            setError(message);
            toast.error(message);
            return false;
        } finally {
            setLoading(false);
        }
    };
    
    const refreshUser = async () => {
        console.log("Attempting to refresh user...");
        const token = localStorage.getItem('token');
        if (token) {
            try {
                const res = await api.get('/auth/check'); // Use an endpoint that returns the full user
                if (res.data.success) {
                    const freshUser = res.data.user;
                    console.log("Refreshed user data:", freshUser);
                    setUser(freshUser);
                    localStorage.setItem('user', JSON.stringify(freshUser));
                } else {
                    // If check fails, it means the token is invalid, so log out.
                    logout();
                }
            } catch (error) {
                console.error('Could not refresh user, token might be expired.', error);
                logout(); // Log out on error to prevent inconsistent state
            }
        }
    };

    const fetchUserCourses = useCallback(async () => {
        const token = localStorage.getItem('token');
        if (!token) return; // Don't fetch if not logged in
        try {
            const response = await api.get('/courses/enrolled');
            setEnrolledCourses(response.data);
        } catch (err) {
            console.error("Failed to fetch enrolled courses", err);
            setEnrolledCourses([]);
        }
    }, []);

    const markCourseCompleted = async (courseId) => {
        try {
            await api.post(`/courses/${courseId}/complete`);
            await fetchUserCourses();
            toast.success('Course marked as completed and certificate issued!');
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to mark course as completed.');
        }
    };
    
    const handle401 = () => {
        console.warn("401 Unauthorized detected. Logging out.");
        logout();
    };

    useEffect(() => {
        // Register the 401 handler
        register401Handler(handle401);

        // Check auth only once on initial load
        if (!hasCheckedAuth.current) {
            checkAuth();
            hasCheckedAuth.current = true;
        }
    }, [checkAuth]);

    // Effect to refetch courses when user ID changes (not entire user object)
    useEffect(() => {
        if (user?.id || user?._id) {
            fetchUserCourses();
        } else {
            setEnrolledCourses([]); // Clear courses if user logs out
        }
    }, [user?.id, user?._id, fetchUserCourses]);
    
    // This function is no longer needed as refreshUser handles it.
    // const updateAvatar = (newUser) => { ... }

    const value = {
        user,
        loading,
        error,
        enrolledCourses,
        login,
        register,
        logout,
        updateProfile,
        checkAuth,
        fetchUserCourses,
        markCourseCompleted,
        refreshUser,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};
